import logger from '@/modules/common/services/logger.service';
import { ComputedRef, ShallowRef, computed, reactive, shallowRef, watchEffect } from 'vue';

/**
 * Retrieves validation rules for a specific field and manages their state.
 *
 * @template RuleFields - The shape of the fields for validation.
 * @template Field - The key of the field being validated.
 * @template Transforms - Transformations applied to field values.
 *
 * @param field - The field key for which rules are being retrieved.
 * @param rulesMap - A map containing rules, their state, and error messages.
 * @param fieldsValidationTrigger - A fields validation trigger.
 * @param isInvalid - Reactive reference for invalid state.
 * @param isValidating - Reactive reference for busy (async) state.
 * @param rules - Array of validation rules for the field.
 * @param transform - Optional transformation function for the field value.
 * @returns An object containing the rules array and error messages.
 */
function getRules<RuleFields, Field extends string & keyof RuleFields, Transforms>(
  field: Field,
  rulesMap: Map<
    keyof RuleFields,
    {
      value: unknown;
      hasValue: boolean;
      wasInvalid: boolean;
      isInvalid: boolean;
      result: {
        rules: Array<(value: unknown) => true | string>;
        errorMessages: string[];
      };
    }
  >,
  fieldsValidationTrigger: (fields: Array<true | string>, context: Context, force: boolean) => void,
  isInvalid: ShallowRef<boolean>,
  isValidating: ShallowRef<boolean>,
  rules: Array<
    (
      value: Value<Field, RuleFields, Transforms>,
      context: Context
    ) => RuleOriginalResult<Exclude<keyof RuleFields, Field>>
  >,
  transform?: RulesTransform<Field, RuleFields, Transforms>
): {
  rules: RulesArray<RuleFields, Field>;
  errorMessages: string[];
} {
  let localRulesData = rulesMap.get(field);
  if (localRulesData === undefined) {
    localRulesData = {
      value: undefined,
      hasValue: false,
      wasInvalid: false,
      isInvalid: false,
      result: reactive({
        rules: [],
        errorMessages: [],
      }),
    };
    rulesMap.set(field, localRulesData);
  }

  const latestResults = new Map<
    (
      value: Value<Field, RuleFields, Transforms>,
      context: Context
    ) => RuleOriginalResult<Exclude<keyof RuleFields, Field>>,
    RuleOriginalResult<Exclude<keyof RuleFields, Field>>
  >();
  const rulesModified = rules.map(
    (rule) =>
      (
        value,
        context = { isSubmitting: false, isTargetedField: false, wasInvalid: false },
        force
      ) => {
        localRulesData.value = value;
        if (transform !== undefined) {
          value = transform(value);
        }
        let result = rule(value, { ...context, wasInvalid: localRulesData.wasInvalid });
        latestResults.set(rule, result);
        if (typeof result === 'object' && !(result instanceof Promise)) {
          if (result.trigger.length > 0) {
            fieldsValidationTrigger(result.trigger, context, force);
          }
          result = result.result ?? true;
        }
        if (result instanceof Promise) {
          // only needed in async context, as sync can't be paralleled,
          // and we will not be able to get stuck in an indeterminate state,
          // where rule is still running, but is not validating
          isValidating.value = true;
          void result
            .then(async (v: Awaited<RuleOriginalResult<keyof RuleFields>>) => {
              if (latestResults.get(rule) !== result) {
                // the result is no longer needed, we are calculating a new one
                return;
              }
              if (typeof v === 'object') {
                fieldsValidationTrigger(v.trigger, context, force);
                v = (await v.result) ?? true;
              }
              if (typeof v === 'string') {
                isInvalid.value = true;
                localRulesData.isInvalid = true;
                localRulesData.result.errorMessages.push(v);
              }
            })
            .catch((e) => {
              logger.error(e);
              isInvalid.value = true;
              localRulesData.isInvalid = true;
              localRulesData.result.errorMessages.push('Validation error');
            })
            .finally(() => {
              const latestResult = latestResults.get(rule);
              if (
                // we are finishing out only current validation for this rule
                latestResult === result ||
                // another ongoing validation won't take care of validation finalization
                (latestResult !== result && !(latestResult instanceof Promise))
              ) {
                isValidating.value = false;
              }
            });
          // errorMessages will take care of the error state, if any
          return true;
        }
        if (typeof result === 'string' && localRulesData.hasValue) {
          isInvalid.value = true;
          localRulesData.isInvalid = true;
          localRulesData.result.errorMessages.push(result);
        }
        return true;
      }
  );
  // reset business, validity and errorMessages first, before any other validation
  rulesModified.unshift(() => {
    isValidating.value = false;
    isInvalid.value = false;
    localRulesData.wasInvalid ||= localRulesData.isInvalid;
    localRulesData.isInvalid = false;
    localRulesData.result.errorMessages = [];
    return true;
  });
  rulesModified.unshift((v) => {
    localRulesData.hasValue = typeof v === 'string' ? !!v.trim() : !!v;
    return true;
  });
  localRulesData.result.rules = rulesModified as Array<(value: unknown) => true | string>;
  return localRulesData.result;
}

type MaybePromise<T> = T | Promise<T>;

type Value<Field extends keyof RuleFields, RuleFields, Transforms> = Field extends keyof Transforms
  ? Transforms[Field]
  : RuleFields[Field];

type RulesArray<RuleFields, K extends keyof RuleFields> = Array<
  (value: RuleFields[K], context: Context) => true | string
>;
type RulesTransform<
  Field extends keyof RuleFields,
  RuleFields,
  Transforms,
> = Field extends keyof Transforms
  ? (value: RuleFields[Field]) => Value<Field, RuleFields, Transforms>
  : undefined;

type RuleOriginalResult<T> = MaybePromise<
  true | string | { trigger: Array<true | (string & T)>; result?: MaybePromise<true | string> }
>;
type RulesOriginalArray<Value, Trigger> = Array<
  (value: Value, context: Context) => RuleOriginalResult<Trigger>
>;
type RulesOriginal<RuleFields, Transforms> = {
  [Field in keyof RuleFields]: {
    rules: RulesOriginalArray<
      Value<Field, RuleFields, Transforms>,
      Exclude<keyof RuleFields, Field>
    >;
    transform?: RulesTransform<Field, RuleFields, Transforms>;
  };
};

interface Context {
  wasInvalid: boolean;
  isSubmitting: boolean;
  isTargetedField: boolean;
}

/**
 * Manages validation rules, state, and transformations for a form.
 *
 * @template RuleFields - The shape of the fields for validation.
 * @template Transforms - Transformations applied to field values.
 * @template Rules - The structure of the rules for validation. The sync ones are lazy (perfect for `required` fields), the async ones trigger right away
 *
 * @param rules - Object defining validation rules and optional transformations.
 * @returns An object containing computed validity and state references, specific field validators, along with rules and error messages for each field.
 *
 * @example
 * const { rules, isValidating, isValid, isInvalid, validateField } = useRules<
 *   { username: string, email: string, password: string, confirmPassword: string, income: string },
 *   { income: Decimal | null },
 * >({
 *   username: {
 *     rules: [
 *       (value) => !!value || 'Required',
 *       (value) => (value.length >= 3 || 'Username must be at least 3 characters long'),
 *       (value) => (/^[a-zA-Z0-9]+$/.test(value) || 'Username can only contain alphanumeric characters'),
 *     ],
 *   },
 *   email: {
 *     rules: [
 *       (value) => !!value || 'Required',
 *       // Async validation
 *       async (value) => {
 *         const isAvailable = await checkEmailAvailability(value);
 *         return isAvailable || 'Email is already in use';
 *       },
 *     ],
 *   },
 *   password: {
 *     rules: [
 *       (value) => !!value || 'Required',
 *       (value) => (value.length >= 8 || 'Password must be at least 8 characters long'),
 *       // Triggers another field's validation, may also be async
 *       () => ({ trigger: 'confirmPassword' }),
 *     ],
 *   },
 *   confirmPassword: {
 *     rules: [
 *       (value) => !!value || 'Required',
 *       (value) => password.value === value || "Doesn't match the password",
 *     ],
 *   },
 *   income: {
 *     // Transform allows us to use value as Decimal | null instead of string
 *     transform: (value) => (!value ? null : new Decimal(value.replaceAll(',', ''))),
 *     rules: [
 *       (value) => value?.gte(0) || `Income cannot be negative.`,
 *     ],
 *   },
 *   firstName:  {
 *     rules: [
 *       // only validate if the form is being submitted
 *       (value, { isSubmitting }) => !isSubmitting || !!value || 'Required',
 *     ],
 *   },
 *   lastName:  {
 *     rules: [
 *       // only validate if the field is being targeted vie `validate('lastName')`
 *       (value, { isTargetedField }) => !isTargetedField || !!value || 'Required',
 *     ],
 *   },
 *   middleName:  {
 *     rules: [
 *       // only validate if the field was invalid since validations were triggered
 *       (value, { wasInvalid }) => !wasInvalid || !!value || 'Required',
 *     ],
 *   }
 * });
 *
 * // Trigger validation of a specific field or fields
 * validate('username', 'password')
 * validate('email')
 *
 *
 * <VTextField
 *   v-model="username"
 *   label="Username"
 *   v-bind="rules.username"
 * />
 * <VTextField
 *   v-model="password"
 *   label="Password"
 *   v-bind="rules.password"
 * />
 * <VTextField
 *   v-model="email"
 *   label="Email"
 *   v-bind="rules.email"
 * />
 * <VTextField
 *   v-model="consfirmPassword"
 *   label="Consfirm Password"
 *   v-bind="rules.consfirmPassword"
 * />
 * <NumericInput
 *   v-model="salary"
 *   label="Salary"
 *   v-bind="rules.salary"
 *   type="decimal"
 * />
 */
export function useRules<
  RuleFields extends object,
  Transforms = object,
  Rules extends RulesOriginal<RuleFields, Transforms> = RulesOriginal<RuleFields, Transforms>,
>(
  rules: Rules
): {
  isSubmitted: ComputedRef<boolean>;
  isInvalid: ComputedRef<boolean>;
  isValid: ComputedRef<boolean>;
  isValidating: ComputedRef<boolean>;
  rules: {
    [K in keyof RuleFields]: {
      rules: Array<(value: RuleFields[K]) => true | string>;
      errorMessages: string[];
    };
  };
  validate(...fields: Array<string & keyof RuleFields>): Promise<boolean>;
  validateAndSubmit: (callback: () => void, onFailure?: () => void) => Promise<boolean>;
  resetValidation: () => void;
} {
  const resultingRules = {} as {
    [K in keyof RuleFields]: {
      rules: Array<(value: RuleFields[K]) => true | string>;
      errorMessages: string[];
    };
  };

  const fieldsInvalidState: Array<ShallowRef<boolean>> = [];
  const fieldsBusyState: Array<ShallowRef<boolean>> = [];
  const fieldsState = new Map<
    string,
    { isInvalid: ShallowRef<boolean>; isValidating: ShallowRef<boolean> }
  >();

  const rulesMap = new Map<
    keyof RuleFields,
    {
      value: unknown;
      hasValue: boolean;
      wasInvalid: boolean;
      isInvalid: boolean;
      result: {
        rules: Array<(value: unknown) => true | string>;
        errorMessages: string[];
      };
    }
  >();

  for (const [field, config] of Object.entries(rules)) {
    const fieldInvalidState = shallowRef(false);
    fieldsInvalidState.push(fieldInvalidState);
    const fieldBusyState = shallowRef(false);
    fieldsBusyState.push(fieldBusyState);

    fieldsState.set(field, { isValidating: fieldBusyState, isInvalid: fieldInvalidState });

    const typedConfig = config as RulesOriginal<RuleFields, Transforms>[keyof RuleFields];
    resultingRules[field] = getRules(
      field as string & keyof RuleFields,
      rulesMap,
      validate as (fields: Array<true | string>, context: Context, force: boolean) => void,
      fieldInvalidState,
      fieldBusyState,
      typedConfig.rules,
      typedConfig.transform as undefined
    ) as {
      rules: Array<(value: Transforms) => true | string>;
      errorMessages: string[];
    };
  }

  const isValidating = computed(() => fieldsBusyState.some((fbs) => fbs.value));
  const isInvalid = computed(() => fieldsInvalidState.some((fvs) => fvs.value));
  const isValid = computed(() => !isValidating.value && !isInvalid.value);

  // so that we could call `validate` from `watchEffect` and not trigger infinite loop
  let isValidatingNonReactive = isValidating.value;
  const onValidated: Array<() => void> = [];
  watchEffect(
    () => {
      isValidatingNonReactive = isValidating.value;
      if (!isValidating.value) {
        onValidated.forEach((cb) => cb());
        onValidated.length = 0;
      }
    },
    { flush: 'sync' }
  );

  async function validate(
    fields: Array<true | (string & keyof RuleFields)>,
    context: Context,
    force = false
  ): Promise<boolean> {
    if (fields.length === 0) {
      isSubmitted.value = true;
      fields = Array.from(rulesMap.keys()) as Array<string & keyof RuleFields>;
    }
    for (const field of fields) {
      if (field === true) continue;
      const rulesData = rulesMap.get(field);
      const state = fieldsState.get(field);
      if (state === undefined || rulesData === undefined) {
        throw new Error(`Unknown field: ${field}`);
      }

      try {
        state.isValidating.value = true;
        state.isInvalid.value = false;
        rulesData.result.errorMessages = [];
        // Making sure `rulesData.hasValue` is not reset to `false`
        const triggerRules = (
          force ? rulesData.result.rules.slice(1) : rulesData.result.rules
        ) as Array<(value: unknown, context: Context, force: boolean) => string | true>;

        if (force) {
          rulesData.hasValue = true; // Making sure the field will be validated
        }

        for (const triggerRule of triggerRules) {
          const triggerResult = triggerRule(rulesData.value, context, force);
          if (typeof triggerResult === 'string') {
            rulesData.isInvalid = true;
            rulesData.result.errorMessages.push(triggerResult);
            state.isInvalid.value = true;
            break;
          }
        }
      } finally {
        state.isValidating.value = false;
      }
    }

    let validated!: () => void;
    const promise = new Promise<void>((r) => (validated = r));
    onValidated.push(validated);
    if (!isValidatingNonReactive) validated(); // all validations were sync
    await promise;

    return isValid.value;
  }

  const isSubmitted = shallowRef(false);

  function resetValidation(): void {
    for (const field of rulesMap.keys()) {
      if (typeof field !== 'string') continue;
      const rulesData = rulesMap.get(field);
      const state = fieldsState.get(field);
      if (state === undefined || rulesData === undefined) continue;
      state.isInvalid.value = false;
      state.isValidating.value = false;
      rulesData.result.errorMessages = [];
    }
    isSubmitted.value = false;
  }

  async function validateAndSubmit(callback: () => void, onFailure?: () => void): Promise<boolean> {
    if (
      await validate([], { isSubmitting: true, isTargetedField: false, wasInvalid: false }, true)
    ) {
      callback();
      return true;
    }
    onFailure?.();
    return false;
  }

  return {
    isSubmitted: computed(() => isSubmitted.value),
    isValidating,
    isValid,
    isInvalid,
    validate: (...fields) =>
      validate(
        fields,
        { isSubmitting: false, isTargetedField: fields.length > 0, wasInvalid: false },
        true
      ),
    validateAndSubmit,
    rules: resultingRules,
    resetValidation,
  };
}

export function required(value: unknown, errorMessage = 'Required'): true | string {
  return (typeof value === 'string' ? !!value.trim() : !!value) || errorMessage;
}

export function minimumValue(value: number, minimum: number): true | string {
  return !(value < minimum) || `Must be at least ${minimum}`;
}

export function maximumValue(value: number, maximum: number): true | string {
  return !(value > maximum) || `Must be at most ${maximum}`;
}

export function minLength(value: unknown, length: number): true | string {
  return (
    (typeof value === 'string' && value.length >= length) || `Must be at least ${length} characters`
  );
}

export function requiredIf(
  value: unknown,
  condition: boolean,
  errorMessage = 'Required'
): true | string {
  return !condition || required(value, errorMessage);
}

const REGEX_EMAIL =
  // eslint-disable-next-line no-control-regex
  /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x21-\x7e\x20\x09])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9][a-z0-9-]*[a-z0-9]|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
export function isEmail(value: unknown, errorMessage = 'Invalid email address'): true | string {
  return (typeof value === 'string' && REGEX_EMAIL.test(value)) || errorMessage;
}
